import axios from 'axios';

// 1. Obtener la URL base, que puede venir con o sin '/api'
const rawApiUrl = window.APP_CONFIG?.API_URL || 'https://web-production-910f8.up.railway.app/api';

// 2. Crear una URL raíz limpia, quitando '/api' si existe al final.
//    Esta se usará para construir las rutas de las imágenes.
export const API_ROOT_URL = rawApiUrl.replace(/\/api$/, '');

// 3. Asegurar que el endpoint de la API siempre termine con '/api' una sola vez.
//    Esta se usará para las llamadas de axios.
const API_ENDPOINT = `${API_ROOT_URL}/api`;
const API_TIMEOUT = window.APP_CONFIG?.API_TIMEOUT || 60000;

// Crear instancia de axios con la configuración correcta
const api = axios.create({
  baseURL: API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: API_TIMEOUT,
  withCredentials: false,
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('Enviando request a:', config.url, 'con método:', config.method);
    return config;
  },
  (error) => {
    console.error('Error en request interceptor:', error);
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
  (response) => {
    console.log('Respuesta exitosa:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('API Error completo:', error);
    console.error('Error response:', error.response);
    console.error('Error request:', error.request);
    console.error('Error config:', error.config);
    
    if (error.response?.status === 401) {
      console.error('Token inválido, redirigiendo al login');
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    } else if (error.response?.status === 403) {
      console.error('Acceso denegado');
    } else if (error.response?.status >= 500) {
      console.error('Error del servidor:', error.response.status);
    } else if (error.code === 'ECONNABORTED') {
      console.error('Timeout de la conexión');
      error.message = 'Tiempo de espera agotado. Verifica tu conexión a internet.';
    } else if (!error.response) {
      console.error('Error de conexión de red');
      error.message = 'No se puede conectar al servidor. Verifica tu conexión a internet.';
    }
    
    return Promise.reject(error);
  }
);

// Función de health check
const healthCheck = async () => {
  const healthApi = axios.create({
    baseURL: API_ENDPOINT, // Corregido para usar el endpoint de API
    timeout: window.APP_CONFIG?.HEALTH_CHECK_TIMEOUT || 10000,
    headers: { 'Content-Type': 'application/json' }
  });
  
  try {
    await healthApi.get('/health/');
    return true;
  } catch (error) {
    console.warn('Health check falló:', error.message);
    return false;
  }
};

// Crear instancia especial para registro con timeout extendido
const registrationApi = axios.create({
  baseURL: API_ENDPOINT, // Corregido para usar el endpoint de API
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: window.APP_CONFIG?.REGISTRATION_TIMEOUT || 120000,
  withCredentials: false,
});

// Aplicar los mismos interceptors a la API de registro
registrationApi.interceptors.request.use(api.interceptors.request.handlers[0].fulfilled, api.interceptors.request.handlers[0].rejected);
registrationApi.interceptors.response.use(api.interceptors.response.handlers[0].fulfilled, api.interceptors.response.handlers[0].rejected);

// Servicios de autenticación
export const authService = {
  login: (credentials) => api.post('/users/auth/login/', credentials),
  register: async (userData) => {
    console.log('Iniciando proceso de registro...');
    const isHealthy = await healthCheck();
    if (!isHealthy) {
      console.warn('Health check falló, pero continuando con el registro...');
    }
    return registrationApi.post('/users/auth/register/', userData);
  },
  logout: () => api.post('/users/auth/logout/'),
  getCurrentUser: () => api.get('/users/auth/user/'),
};

// Servicios de productos
export const productService = {
  getProducts: (params) => api.get('/products/', { params }),
  getProduct: (id) => api.get(`/products/${id}/`),
  createProduct: (productData) =>
    api.post('/products/', productData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateProduct: (id, productData) => api.put(`/products/${id}/`, productData),
  deleteProduct: (id) => api.delete(`/products/${id}/`),
  getCategories: () => api.get('/products/categories/'),
};

// Servicios de RFQ (Request for Quotation)
export const rfqService = {
  getRFQs: (params) => api.get('/rfq/', { params }),
  getRFQ: (id) => api.get(`/rfq/${id}/`),
  createRFQ: (rfqData) => api.post('/rfq/', rfqData),
  updateRFQ: (id, rfqData) => api.put(`/rfq/${id}/`, rfqData),
  deleteRFQ: (id) => api.delete(`/rfq/${id}/`),
};

// Servicios de chat
export const chatService = {
  getConversations: () => api.get('/chat/conversations/'),
  getMessages: (conversationId) => api.get(`/chat/conversations/${conversationId}/messages/`),
  sendMessage: (conversationId, message) => api.post(`/chat/conversations/${conversationId}/messages/`, message),
  createConversation: (participantId) => api.post('/chat/conversations/', { participant: participantId }),
};

// Servicios de reseñas
export const reviewService = {
  getReviews: (params) => api.get('/reviews/', { params }),
  createReview: (reviewData) => api.post('/reviews/', reviewData),
  updateReview: (id, reviewData) => api.put(`/reviews/${id}/`, reviewData),
  deleteReview: (id) => api.delete(`/reviews/${id}/`),
};

// Servicios de notificaciones
export const notificationService = {
  getNotifications: () => api.get('/notifications/'),
  markAsRead: (id) => api.put(`/notifications/${id}/`, { read: true }),
  markAllAsRead: () => api.put('/notifications/mark-all-read/'),
};

// Servicios de usuarios
export const userService = {
  getProfile: () => api.get('/users/profile/'),
  updateProfile: (profileData) => api.put('/users/profile/', profileData),
  getBusinessProfile: () => api.get('/users/business-profile/'),
  updateBusinessProfile: (profileData) => api.put('/users/business-profile/', profileData),
};

// Servicios de mercado
export const marketService = {
  getPrices: () => api.get('/market/prices/'),
};

export default api;